import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RichTextarea } from '../components/RichTextarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, LogOut, Upload, Image, FileText, UtensilsCrossed, Save, Heart, Globe, Leaf, ChefHat, Languages, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t, getLocalizedText } = useLanguage();
  const { token, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Recipe state
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [formData, setFormData] = useState({
    title: { fi: '', fr: '', en: '' },
    description: { fi: '', fr: '', en: '' },
    ingredients: { fi: '', fr: '', en: '' },
    instructions: { fi: '', fr: '', en: '' },
    category: 'casual',
    course_type: 'main',
    prep_time: 15,
    cook_time: 30,
    servings: 4,
    image_url: '',
    image_data: '',
    featured: false
  });
  const [editLang, setEditLang] = useState('fi');
  const [translating, setTranslating] = useState(false);
  const fileInputRef = useRef(null);

  // Page content state
  const [pages, setPages] = useState({
    health: null,
    why_vegan: null,
    techniques: null
  });
  const [selectedPage, setSelectedPage] = useState('health');
  const [pageEditLang, setPageEditLang] = useState('fi');
  const [pageSaving, setPageSaving] = useState(false);
  const [pageTranslating, setPageTranslating] = useState(false);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Get user token from localStorage (for admin actions)
  const userToken = localStorage.getItem('love4vegan_user_token') || token;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchRecipes();
    fetchAllPages();
    fetchUsers();
  }, [isAuthenticated, navigate]);

  // ============== RECIPES ==============
  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============== USERS ==============
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      toast.success('User deleted');
      setDeleteUserDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const openAddDialog = () => {
    setSelectedRecipe(null);
    setFormData({
      title: { fi: '', fr: '', en: '' },
      description: { fi: '', fr: '', en: '' },
      ingredients: { fi: '', fr: '', en: '' },
      instructions: { fi: '', fr: '', en: '' },
      category: 'casual',
      course_type: 'main',
      prep_time: 15,
      cook_time: 30,
      servings: 4,
      image_url: '',
      image_data: '',
      featured: false
    });
    setEditLang('fi');
    setDialogOpen(true);
  };

  const openEditDialog = (recipe) => {
    setSelectedRecipe(recipe);
    setFormData({
      title: recipe.title || { fi: '', fr: '', en: '' },
      description: recipe.description || { fi: '', fr: '', en: '' },
      ingredients: recipe.ingredients || { fi: '', fr: '', en: '' },
      instructions: recipe.instructions || { fi: '', fr: '', en: '' },
      category: recipe.category || 'casual',
      course_type: recipe.course_type || 'main',
      prep_time: recipe.prep_time || 15,
      cook_time: recipe.cook_time || 30,
      servings: recipe.servings || 4,
      image_url: recipe.image_url || '',
      image_data: recipe.image_data || '',
      featured: recipe.featured || false
    });
    setEditLang('fi');
    setDialogOpen(true);
  };

  const openDeleteDialog = (recipe) => {
    setSelectedRecipe(recipe);
    setDeleteDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formDataObj, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image_data: response.data.image_data, image_url: '' }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedRecipe) {
        await axios.put(`${API}/recipes/${selectedRecipe.id}`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Recipe updated!');
      } else {
        await axios.post(`${API}/recipes`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Recipe created!');
      }
      setDialogOpen(false);
      fetchRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  const handleDelete = async () => {
    if (!selectedRecipe) return;
    
    try {
      await axios.delete(`${API}/recipes/${selectedRecipe.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Recipe deleted!');
      setDeleteDialogOpen(false);
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  const updateTranslation = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], [editLang]: value }
    }));
  };

  // Auto-translate recipe from current language to others
  const handleTranslate = async () => {
    const sourceLang = editLang;
    const targetLangs = ['fi', 'fr', 'en'].filter(l => l !== sourceLang);
    
    // Check if source has content
    if (!formData.title[sourceLang] && !formData.description[sourceLang]) {
      toast.error(getLocalizedText({
        fi: 'Kirjoita ensin sisältöä käännettäväksi',
        fr: "Écrivez d'abord du contenu à traduire",
        en: 'Write some content first to translate'
      }));
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(`${API}/translate`, {
        source_lang: sourceLang,
        target_langs: targetLangs,
        title: formData.title[sourceLang] || '',
        description: formData.description[sourceLang] || '',
        ingredients: formData.ingredients[sourceLang] || '',
        instructions: formData.instructions[sourceLang] || ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update form data with translations
      setFormData(prev => ({
        ...prev,
        title: { ...prev.title, ...response.data.title },
        description: { ...prev.description, ...response.data.description },
        ingredients: { ...prev.ingredients, ...response.data.ingredients },
        instructions: { ...prev.instructions, ...response.data.instructions }
      }));

      toast.success(getLocalizedText({
        fi: 'Käännös valmis! Tarkista muut kielet.',
        fr: 'Traduction terminée! Vérifiez les autres langues.',
        en: 'Translation complete! Check the other languages.'
      }));
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(getLocalizedText({
        fi: 'Käännös epäonnistui',
        fr: 'La traduction a échoué',
        en: 'Translation failed'
      }));
    } finally {
      setTranslating(false);
    }
  };

  // ============== PAGES ==============
  const fetchAllPages = async () => {
    try {
      const [health, whyVegan, techniques] = await Promise.all([
        axios.get(`${API}/pages/health`),
        axios.get(`${API}/pages/why_vegan`),
        axios.get(`${API}/pages/techniques`)
      ]);
      
      // Default sections for each page (pre-populated for editing)
      const defaultSections = {
        health: [
          { title: { fi: 'Sydänterveys', fr: 'Santé Cardiaque', en: 'Heart Health' }, content: { fi: 'Vegaaninen ruokavalio voi alentaa verenpainetta ja kolesterolia, vähentäen sydänsairauksien riskiä.', fr: 'Un régime végétalien peut réduire la tension artérielle et le cholestérol, diminuant le risque de maladies cardiaques.', en: 'A vegan diet can lower blood pressure and cholesterol, reducing the risk of heart disease.' }},
          { title: { fi: 'Paino ja Energia', fr: 'Poids et Énergie', en: 'Weight & Energy' }, content: { fi: 'Kasvisruokavalio auttaa ylläpitämään terveellistä painoa ja lisää energiatasoja luonnollisesti.', fr: "Un régime végétal aide à maintenir un poids santé et augmente naturellement les niveaux d'énergie.", en: 'A plant-based diet helps maintain a healthy weight and naturally increases energy levels.' }},
          { title: { fi: 'Ravintoaineet', fr: 'Nutriments', en: 'Nutrients' }, content: { fi: 'Monipuolinen vegaaninen ruokavalio tarjoaa runsaasti kuitua, vitamiineja ja antioksidantteja.', fr: "Un régime végétalien varié offre beaucoup de fibres, de vitamines et d'antioxydants.", en: 'A varied vegan diet provides plenty of fiber, vitamins, and antioxidants.' }},
          { title: { fi: 'Mistä löydän ravinteet', fr: 'Où trouver les nutriments', en: 'Where to find nutrients' }, content: { fi: 'Proteiini: palkokasvit, tofu, tempeh, seitan, pähkinät. B12: ravintolisä tai rikastetut tuotteet. Rauta: linssit, pinaatti, tahini. Kalsium: lehtikasvit, manteli, rikastetut kasvimaidot. Omega-3: pellavansiemenet, chia, saksanpähkinät.', fr: 'Protéines: légumineuses, tofu, tempeh, seitan, noix. B12: supplément ou produits enrichis. Fer: lentilles, épinards, tahini. Calcium: légumes verts, amandes, laits végétaux enrichis. Oméga-3: graines de lin, chia, noix.', en: 'Protein: legumes, tofu, tempeh, seitan, nuts. B12: supplement or fortified products. Iron: lentils, spinach, tahini. Calcium: leafy greens, almonds, fortified plant milks. Omega-3: flaxseeds, chia, walnuts.' }},
          { title: { fi: 'Ruoansulatus', fr: 'Digestion', en: 'Digestion' }, content: { fi: 'Runsas kuidunsaanti parantaa suoliston terveyttä ja ruoansulatusta.', fr: 'Un apport élevé en fibres améliore la santé intestinale et la digestion.', en: 'High fiber intake improves gut health and digestion.' }},
          { title: { fi: 'Tulehdukset', fr: 'Inflammation', en: 'Inflammation' }, content: { fi: 'Kasvisruoka vähentää kroonista tulehdusta kehossa.', fr: "Les aliments végétaux réduisent l'inflammation chronique dans le corps.", en: 'Plant foods reduce chronic inflammation in the body.' }},
          { title: { fi: 'Pitkäikäisyys', fr: 'Longévité', en: 'Longevity' }, content: { fi: 'Tutkimukset osoittavat, että kasvissyöjät elävät keskimäärin pidempään.', fr: 'Les études montrent que les végétaliens vivent en moyenne plus longtemps.', en: 'Studies show that vegans tend to live longer on average.' }}
        ],
        why_vegan: [
          { title: { fi: 'Empatia', fr: 'Empathie', en: 'Compassion' }, content: { fi: 'Jokainen eläin tuntee kipua ja iloa. Veganismilla vähennämme kärsimystä. Jokainen elävä olento ansaitsee kunnioituksen ja oikeuden elää vapaana.', fr: "Chaque animal ressent la douleur et la joie. Le véganisme réduit la souffrance. Chaque être vivant mérite le respect et le droit de vivre libre.", en: "Every animal feels pain and joy. Veganism reduces suffering. Every living being deserves respect and the right to live free." }},
          { title: { fi: 'Hiilijalanjälki', fr: 'Empreinte Carbone', en: 'Carbon Footprint' }, content: { fi: 'Eläintuotanto aiheuttaa 14.5% maailman kasvihuonepäästöistä - enemmän kuin kaikki liikenne yhteensä.', fr: "L'élevage cause 14.5% des émissions mondiales de gaz à effet de serre - plus que tous les transports réunis.", en: 'Animal agriculture causes 14.5% of global greenhouse gas emissions - more than all transportation combined.' }},
          { title: { fi: 'Metsät', fr: 'Forêts', en: 'Forests' }, content: { fi: '80% Amazonin metsäkadosta johtuu karjankasvatuksesta. Vegaaninen ruokavalio suojelee metsiä.', fr: "80% de la déforestation amazonienne est due à l'élevage. Un régime végétalien protège les forêts.", en: '80% of Amazon deforestation is due to cattle ranching. A vegan diet protects forests.' }},
          { title: { fi: 'Vesi', fr: 'Eau', en: 'Water' }, content: { fi: 'Yhden naudan hampurilaisen tuottaminen vaatii 2500 litraa vettä. Kasvisruoka säästää vettä.', fr: "La production d'un hamburger de boeuf nécessite 2500 litres d'eau. Les aliments végétaux économisent l'eau.", en: 'Producing one beef burger requires 2500 liters of water. Plant foods save water.' }}
        ],
        techniques: [
          { title: { fi: 'Liemien Valmistus', fr: 'Préparation de Bouillons', en: 'Making Broths' }, content: { fi: 'Hyvä kasvisliemi on monen ruoan perusta. Käytä sipulia, porkkanaa, selleriä, yrttejä ja mausteita. Keitä hitaasti 1-2 tuntia.', fr: 'Un bon bouillon de légumes est la base de nombreux plats. Utilisez oignon, carotte, céleri, herbes et épices. Faites mijoter 1-2 heures.', en: 'A good vegetable broth is the foundation of many dishes. Use onion, carrot, celery, herbs and spices. Simmer slowly for 1-2 hours.' }},
          { title: { fi: 'Tofun Käsittely', fr: 'Préparation du Tofu', en: 'Tofu Preparation' }, content: { fi: 'Purista tofu kuivaksi, leikkaa kuutioiksi ja marinoi vähintään 30 minuuttia. Paista korkealla lämmöllä rapeaksi.', fr: 'Pressez le tofu pour le sécher, coupez en cubes et marinez au moins 30 minutes. Faites frire à feu vif.', en: 'Press tofu dry, cut into cubes and marinate for at least 30 minutes. Fry at high heat until crispy.' }},
          { title: { fi: 'Vegaaninen Leivonta', fr: 'Pâtisserie Végane', en: 'Vegan Baking' }, content: { fi: 'Korvaa munat pellavansiemenillä (1 rkl + 3 rkl vettä = 1 muna), banaanilla tai omenasoseella.', fr: "Remplacez les œufs par des graines de lin (1 c.s. + 3 c.s. d'eau = 1 œuf), de la banane ou de la compote de pommes.", en: 'Replace eggs with flaxseeds (1 tbsp + 3 tbsp water = 1 egg), banana, or applesauce.' }},
          { title: { fi: 'Kasvisten Paahto', fr: 'Rôtissage des Légumes', en: 'Roasting Vegetables' }, content: { fi: 'Paahda kasvikset 200°C:ssa öljyllä ja suolalla. Älä täytä pellillä liikaa. Anna karamelloitua 25-40 minuuttia.', fr: "Rôtissez les légumes à 200°C avec de l'huile et du sel. Ne surchargez pas la plaque. Laissez caraméliser 25-40 minutes.", en: "Roast vegetables at 200°C with oil and salt. Don't overcrowd the pan. Let caramelize for 25-40 minutes." }},
          { title: { fi: 'Marinoiminen', fr: 'Marinage', en: 'Marinating' }, content: { fi: 'Hyvä marinadi sisältää happoa (sitruuna, etikka), öljyä, suolaa ja mausteita. Anna marinoitua vähintään tunnin.', fr: "Une bonne marinade contient de l'acide (citron, vinaigre), de l'huile, du sel et des épices. Laissez mariner au moins une heure.", en: 'A good marinade contains acid (lemon, vinegar), oil, salt and spices. Marinate for at least an hour.' }},
          { title: { fi: 'Umami Maku', fr: 'Saveur Umami', en: 'Umami Flavor' }, content: { fi: 'Lisää umamia soijakastikkeella, miso-tahnalla, hiivahiutaleilla, tomaattipyreellä tai sienillä.', fr: "Ajoutez de l'umami avec de la sauce soja, du miso, de la levure nutritionnelle, du concentré de tomates ou des champignons.", en: 'Add umami with soy sauce, miso paste, nutritional yeast, tomato paste, or mushrooms.' }}
        ]
      };

      const defaultTitles = {
        health: { fi: 'Terveys ja Veganismi', fr: 'Santé et Véganisme', en: 'Health and Veganism' },
        why_vegan: { fi: 'Miksi Veganismi?', fr: 'Pourquoi le Véganisme?', en: 'Why Veganism?' },
        techniques: { fi: 'Vegaaniset Ruoanlaittotekniikat', fr: 'Techniques de Cuisine Végane', en: 'Vegan Cooking Techniques' }
      };

      const defaultIntros = {
        health: { fi: 'Vegaaninen ruokavalio tarjoaa monia terveyshyötyjä, kun se toteutetaan oikein.', fr: "Un régime végétalien offre de nombreux avantages pour la santé lorsqu'il est bien mené.", en: 'A vegan diet offers many health benefits when done correctly.' },
        why_vegan: { fi: 'Valitsemme veganismin eläinten hyvinvoinnin ja ekologisten syiden vuoksi.', fr: 'Nous choisissons le véganisme pour le bien-être animal et des raisons écologiques.', en: 'We choose veganism for animal welfare and ecological reasons.' },
        techniques: { fi: 'Opi vegaanisen ruoanlaiton perusteet ja edistyneet tekniikat.', fr: 'Apprenez les bases et les techniques avancées de la cuisine végétalienne.', en: 'Learn the basics and advanced techniques of vegan cooking.' }
      };

      // Merge with defaults if sections are empty
      const mergeWithDefaults = (data, pageKey) => {
        return {
          ...data,
          title: data.title?.fi ? data.title : defaultTitles[pageKey],
          content: data.content?.fi ? data.content : defaultIntros[pageKey],
          sections: (data.sections && data.sections.length > 0) ? data.sections : defaultSections[pageKey]
        };
      };

      setPages({
        health: mergeWithDefaults(health.data, 'health'),
        why_vegan: mergeWithDefaults(whyVegan.data, 'why_vegan'),
        techniques: mergeWithDefaults(techniques.data, 'techniques')
      });
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const updatePageContent = (field, value, sectionIndex = null, sectionField = null) => {
    setPages(prev => {
      const page = { ...prev[selectedPage] };
      
      if (sectionIndex !== null && sectionField !== null) {
        // Update section
        const sections = [...(page.sections || [])];
        if (!sections[sectionIndex]) {
          sections[sectionIndex] = { title: { fi: '', fr: '', en: '' }, content: { fi: '', fr: '', en: '' } };
        }
        sections[sectionIndex] = {
          ...sections[sectionIndex],
          [sectionField]: {
            ...sections[sectionIndex][sectionField],
            [pageEditLang]: value
          }
        };
        page.sections = sections;
      } else {
        // Update main field
        page[field] = {
          ...page[field],
          [pageEditLang]: value
        };
      }
      
      return { ...prev, [selectedPage]: page };
    });
  };

  const addSection = () => {
    setPages(prev => {
      const page = { ...prev[selectedPage] };
      const sections = [...(page.sections || [])];
      sections.push({
        title: { fi: '', fr: '', en: '' },
        content: { fi: '', fr: '', en: '' }
      });
      page.sections = sections;
      return { ...prev, [selectedPage]: page };
    });
  };

  const removeSection = (index) => {
    setPages(prev => {
      const page = { ...prev[selectedPage] };
      const sections = [...(page.sections || [])];
      sections.splice(index, 1);
      page.sections = sections;
      return { ...prev, [selectedPage]: page };
    });
  };

  const savePage = async () => {
    setPageSaving(true);
    try {
      const pageData = pages[selectedPage];
      await axios.put(`${API}/pages/${selectedPage}`, {
        title: pageData.title,
        content: pageData.content,
        sections: pageData.sections
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Page saved!');
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setPageSaving(false);
    }
  };

  // Auto-translate page content from current language to others
  const handlePageTranslate = async () => {
    const sourceLang = pageEditLang;
    const targetLangs = ['fi', 'fr', 'en'].filter(l => l !== sourceLang);
    const pageData = pages[selectedPage];
    
    if (!pageData?.title?.[sourceLang] && !pageData?.content?.[sourceLang]) {
      toast.error(getLocalizedText({
        fi: 'Kirjoita ensin sisältöä käännettäväksi',
        fr: "Écrivez d'abord du contenu à traduire",
        en: 'Write some content first to translate'
      }));
      return;
    }

    setPageTranslating(true);
    try {
      // Translate main title and content
      const mainResponse = await axios.post(`${API}/translate`, {
        source_lang: sourceLang,
        target_langs: targetLangs,
        title: pageData.title?.[sourceLang] || '',
        description: pageData.content?.[sourceLang] || '',
        ingredients: '',
        instructions: ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update main fields
      const updatedPage = {
        ...pageData,
        title: { ...pageData.title, ...mainResponse.data.title },
        content: { ...pageData.content, ...mainResponse.data.description }
      };

      // Translate sections if any
      if (pageData.sections && pageData.sections.length > 0) {
        const translatedSections = [...pageData.sections];
        
        for (let i = 0; i < pageData.sections.length; i++) {
          const section = pageData.sections[i];
          if (section.title?.[sourceLang] || section.content?.[sourceLang]) {
            const sectionResponse = await axios.post(`${API}/translate`, {
              source_lang: sourceLang,
              target_langs: targetLangs,
              title: section.title?.[sourceLang] || '',
              description: section.content?.[sourceLang] || '',
              ingredients: '',
              instructions: ''
            }, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            translatedSections[i] = {
              ...section,
              title: { ...section.title, ...sectionResponse.data.title },
              content: { ...section.content, ...sectionResponse.data.description }
            };
          }
        }
        updatedPage.sections = translatedSections;
      }

      setPages(prev => ({ ...prev, [selectedPage]: updatedPage }));

      toast.success(getLocalizedText({
        fi: 'Käännös valmis! Tarkista muut kielet.',
        fr: 'Traduction terminée! Vérifiez les autres langues.',
        en: 'Translation complete! Check the other languages.'
      }));
    } catch (error) {
      console.error('Page translation error:', error);
      toast.error(getLocalizedText({
        fi: 'Käännös epäonnistui',
        fr: 'La traduction a échoué',
        en: 'Translation failed'
      }));
    } finally {
      setPageTranslating(false);
    }
  };

  const pageConfig = {
    health: { 
      icon: Heart, 
      label: { fi: 'Terveys', fr: 'Santé', en: 'Health' },
      color: 'text-red-500'
    },
    why_vegan: { 
      icon: Globe, 
      label: { fi: 'Miksi Veganismi', fr: 'Pourquoi Végan', en: 'Why Vegan' },
      color: 'text-blue-500'
    },
    techniques: { 
      icon: ChefHat, 
      label: { fi: 'Tekniikat', fr: 'Techniques', en: 'Techniques' },
      color: 'text-amber-500'
    }
  };

  if (!isAuthenticated) return null;

  const currentPage = pages[selectedPage];

  return (
    <div className="min-h-screen py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">{t('admin.dashboard')}</h1>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="rounded-full"
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('admin.logout')}
          </Button>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="recipes" className="space-y-6">
          <TabsList className="bg-card border border-border p-1 rounded-xl">
            <TabsTrigger value="recipes" className="rounded-lg px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <UtensilsCrossed className="mr-2 h-4 w-4" />
              {t('nav.recipes')}
            </TabsTrigger>
            <TabsTrigger value="pages" className="rounded-lg px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FileText className="mr-2 h-4 w-4" />
              {getLocalizedText({ fi: 'Sivut', fr: 'Pages', en: 'Pages' })}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="mr-2 h-4 w-4" />
              {getLocalizedText({ fi: 'Käyttäjät', fr: 'Utilisateurs', en: 'Users' })}
            </TabsTrigger>
          </TabsList>

          {/* ============== RECIPES TAB ============== */}
          <TabsContent value="recipes" className="space-y-6">
            <div className="flex justify-end">
              <Button 
                onClick={openAddDialog}
                className="bg-primary hover:bg-primary/90 rounded-full"
                data-testid="add-recipe-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('admin.addRecipe')}
              </Button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-card rounded-2xl h-48 animate-pulse" />
                ))}
              </div>
            ) : recipes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recipes.map(recipe => (
                  <div 
                    key={recipe.id} 
                    className="bg-card rounded-2xl border border-border overflow-hidden"
                    data-testid={`recipe-item-${recipe.id}`}
                  >
                    <div className="aspect-video bg-muted/20 relative">
                      {(recipe.image_data || recipe.image_url) ? (
                        <img 
                          src={recipe.image_data || recipe.image_url} 
                          alt={getLocalizedText(recipe.title)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="h-12 w-12 text-muted" />
                        </div>
                      )}
                      <span className="absolute top-2 left-2 px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">
                        {t(`recipes.${recipe.category}`)}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-2 line-clamp-1">
                        {getLocalizedText(recipe.title)}
                      </h3>
                      <p className="text-sm text-foreground/60 line-clamp-2 mb-4">
                        {getLocalizedText(recipe.description)}
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openEditDialog(recipe)}
                          className="flex-1"
                          data-testid={`edit-recipe-${recipe.id}`}
                        >
                          <Edit className="mr-1 h-3 w-3" />
                          {t('admin.editRecipe')}
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => openDeleteDialog(recipe)}
                          data-testid={`delete-recipe-${recipe.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-card rounded-2xl border border-border">
                <p className="text-foreground/60 mb-4">No recipes yet</p>
                <Button onClick={openAddDialog} className="rounded-full">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('admin.addRecipe')}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ============== PAGES TAB ============== */}
          <TabsContent value="pages" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Page Selector Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-card rounded-2xl border border-border p-4 space-y-2">
                  <h3 className="font-semibold mb-4 text-sm text-foreground/60 uppercase tracking-wide">
                    {getLocalizedText({ fi: 'Valitse sivu', fr: 'Choisir la page', en: 'Select Page' })}
                  </h3>
                  {Object.entries(pageConfig).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedPage(key)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${
                          selectedPage === key 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted/20'
                        }`}
                        data-testid={`page-select-${key}`}
                      >
                        <Icon className={`h-5 w-5 ${selectedPage === key ? '' : config.color}`} />
                        <span className="font-medium">{getLocalizedText(config.label)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Page Editor */}
              <div className="lg:col-span-3">
                <div className="bg-card rounded-2xl border border-border p-6">
                  {/* Language Selector */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex gap-2 p-1 bg-muted/20 rounded-lg">
                      {['fi', 'fr', 'en'].map(lang => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => setPageEditLang(lang)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            pageEditLang === lang ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/40'
                          }`}
                          data-testid={`page-lang-tab-${lang}`}
                        >
                          {lang === 'fi' ? '🇫🇮 Suomi' : lang === 'fr' ? '🇫🇷 Français' : '🇬🇧 English'}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {/* Translate Button */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePageTranslate}
                        disabled={pageTranslating}
                        className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground rounded-full"
                        data-testid="translate-page-btn"
                      >
                        {pageTranslating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {getLocalizedText({ fi: 'Käännetään...', fr: 'Traduction...', en: 'Translating...' })}
                          </>
                        ) : (
                          <>
                            <Languages className="mr-2 h-4 w-4" />
                            {getLocalizedText({ 
                              fi: `Käännä ${pageEditLang.toUpperCase()} → muut`, 
                              fr: `Traduire ${pageEditLang.toUpperCase()} → autres`, 
                              en: `Translate ${pageEditLang.toUpperCase()} → others` 
                            })}
                          </>
                        )}
                      </Button>
                      {/* Save Button */}
                      <Button 
                        onClick={savePage} 
                        disabled={pageSaving}
                        className="bg-primary rounded-full"
                        data-testid="save-page-btn"
                      >
                        <Save className="mr-2 h-4 w-4" />
                        {pageSaving ? getLocalizedText({ fi: 'Tallennetaan...', fr: 'Sauvegarde...', en: 'Saving...' }) : t('admin.save')}
                      </Button>
                    </div>
                  </div>

                  {currentPage && (
                    <div className="space-y-6">
                      {/* Page Title */}
                      <div className="space-y-2">
                        <Label className="text-base font-semibold">
                          {getLocalizedText({ fi: 'Sivun otsikko', fr: 'Titre de la page', en: 'Page Title' })} ({pageEditLang.toUpperCase()})
                        </Label>
                        <Input
                          value={currentPage.title?.[pageEditLang] || ''}
                          onChange={(e) => updatePageContent('title', e.target.value)}
                          placeholder="Page title..."
                          className="text-lg"
                          data-testid="page-title-input"
                        />
                      </div>

                      {/* Page Introduction */}
                      <div className="space-y-2">
                        <Label className="text-base font-semibold">
                          {getLocalizedText({ fi: 'Johdanto', fr: 'Introduction', en: 'Introduction' })} ({pageEditLang.toUpperCase()})
                        </Label>
                        <Textarea
                          value={currentPage.content?.[pageEditLang] || ''}
                          onChange={(e) => updatePageContent('content', e.target.value)}
                          placeholder="Introduction text..."
                          rows={3}
                          data-testid="page-intro-input"
                        />
                      </div>

                      {/* Sections */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">
                            {getLocalizedText({ fi: 'Osiot', fr: 'Sections', en: 'Sections' })}
                          </Label>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={addSection}
                            className="rounded-full"
                            data-testid="add-section-btn"
                          >
                            <Plus className="mr-1 h-4 w-4" />
                            {getLocalizedText({ fi: 'Lisää osio', fr: 'Ajouter section', en: 'Add Section' })}
                          </Button>
                        </div>

                        {(currentPage.sections || []).map((section, index) => (
                          <div key={index} className="p-4 bg-muted/10 rounded-xl border border-border/50 space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-foreground/60">
                                {getLocalizedText({ fi: 'Osio', fr: 'Section', en: 'Section' })} {index + 1}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => removeSection(index)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`remove-section-${index}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="space-y-2">
                              <Label>
                                {getLocalizedText({ fi: 'Otsikko', fr: 'Titre', en: 'Title' })} ({pageEditLang.toUpperCase()})
                              </Label>
                              <Input
                                value={section.title?.[pageEditLang] || ''}
                                onChange={(e) => updatePageContent('title', e.target.value, index, 'title')}
                                placeholder="Section title..."
                                data-testid={`section-title-${index}`}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>
                                {getLocalizedText({ fi: 'Sisältö', fr: 'Contenu', en: 'Content' })} ({pageEditLang.toUpperCase()})
                              </Label>
                              <Textarea
                                value={section.content?.[pageEditLang] || ''}
                                onChange={(e) => updatePageContent('content', e.target.value, index, 'content')}
                                placeholder="Section content..."
                                rows={4}
                                data-testid={`section-content-${index}`}
                              />
                            </div>
                          </div>
                        ))}

                        {(!currentPage.sections || currentPage.sections.length === 0) && (
                          <p className="text-center py-8 text-foreground/40">
                            {getLocalizedText({ 
                              fi: 'Ei osioita vielä. Lisää osio yllä olevalla painikkeella.', 
                              fr: "Pas encore de sections. Ajoutez une section avec le bouton ci-dessus.", 
                              en: 'No sections yet. Add a section using the button above.' 
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============== USERS TAB ============== */}
          <TabsContent value="users" className="space-y-6">
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="h-5 w-5" />
                {getLocalizedText({ fi: 'Rekisteröityneet käyttäjät', fr: 'Utilisateurs enregistrés', en: 'Registered Users' })}
                <span className="text-sm font-normal text-foreground/60">({users.length})</span>
              </h2>

              {usersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-center py-8 text-foreground/60">
                  {getLocalizedText({ fi: 'Ei käyttäjiä', fr: 'Aucun utilisateur', en: 'No users' })}
                </p>
              ) : (
                <div className="space-y-3">
                  {users.map(u => (
                    <div 
                      key={u.id} 
                      className="flex items-center justify-between p-4 bg-muted/20 rounded-xl"
                      data-testid={`user-${u.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-foreground/60">{u.email}</p>
                        </div>
                        {u.is_admin && (
                          <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-foreground/40">
                          {u.favorites?.length || 0} {getLocalizedText({ fi: 'suosikkia', fr: 'favoris', en: 'favorites' })}
                        </span>
                        {!u.is_admin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => { setSelectedUser(u); setDeleteUserDialogOpen(true); }}
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Recipe Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedRecipe ? t('admin.editRecipe') : t('admin.addRecipe')}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Language Selector + Translate Button */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex gap-2 p-1 bg-muted/20 rounded-lg">
                  {['fi', 'fr', 'en'].map(lang => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setEditLang(lang)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        editLang === lang ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/40'
                      }`}
                      data-testid={`lang-tab-${lang}`}
                    >
                      {lang === 'fi' ? '🇫🇮 Suomi' : lang === 'fr' ? '🇫🇷 Français' : '🇬🇧 English'}
                    </button>
                  ))}
                </div>
                
                {/* Auto-Translate Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTranslate}
                  disabled={translating}
                  className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
                  data-testid="translate-btn"
                >
                  {translating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {getLocalizedText({ fi: 'Käännetään...', fr: 'Traduction...', en: 'Translating...' })}
                    </>
                  ) : (
                    <>
                      <Languages className="mr-2 h-4 w-4" />
                      {getLocalizedText({ 
                        fi: `Käännä ${editLang.toUpperCase()} → muut`, 
                        fr: `Traduire ${editLang.toUpperCase()} → autres`, 
                        en: `Translate ${editLang.toUpperCase()} → others` 
                      })}
                    </>
                  )}
                </Button>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label>{t('admin.recipeTitle')} ({editLang.toUpperCase()})</Label>
                <Input
                  value={formData.title[editLang]}
                  onChange={(e) => updateTranslation('title', e.target.value)}
                  placeholder="Recipe title..."
                  required={editLang === 'fi'}
                  data-testid="recipe-title-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>{t('admin.recipeDescription')} ({editLang.toUpperCase()})</Label>
                <Textarea
                  value={formData.description[editLang]}
                  onChange={(e) => updateTranslation('description', e.target.value)}
                  placeholder="Short description..."
                  rows={2}
                  data-testid="recipe-description-input"
                />
              </div>

              {/* Ingredients */}
              <div className="space-y-2">
                <Label>{t('admin.recipeIngredients')} ({editLang.toUpperCase()})</Label>
                <RichTextarea
                  value={formData.ingredients[editLang]}
                  onChange={(e) => updateTranslation('ingredients', e.target.value)}
                  placeholder="- 2 cups flour&#10;- 1 tsp salt&#10;..."
                  rows={4}
                  data-testid="recipe-ingredients-input"
                />
              </div>

              {/* Instructions */}
              <div className="space-y-2">
                <Label>{t('admin.recipeInstructions')} ({editLang.toUpperCase()})</Label>
                <RichTextarea
                  value={formData.instructions[editLang]}
                  onChange={(e) => updateTranslation('instructions', e.target.value)}
                  placeholder="1. Mix ingredients...&#10;2. Bake at 180°C..."
                  rows={4}
                  data-testid="recipe-instructions-input"
                />
              </div>

              {/* Category & Course Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.recipeCategory')}</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}
                  >
                    <SelectTrigger data-testid="recipe-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="express">{t('recipes.express')}</SelectItem>
                      <SelectItem value="guests">{t('recipes.guests')}</SelectItem>
                      <SelectItem value="casual">{t('recipes.casual')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('recipes.courseType')}</Label>
                  <Select 
                    value={formData.course_type} 
                    onValueChange={(v) => setFormData(p => ({ ...p, course_type: v }))}
                  >
                    <SelectTrigger data-testid="recipe-course-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appetizer">{t('recipes.appetizer')}</SelectItem>
                      <SelectItem value="main">{t('recipes.main')}</SelectItem>
                      <SelectItem value="dessert">{t('recipes.dessert')}</SelectItem>
                      <SelectItem value="snack">{t('recipes.snack')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Featured Toggle */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData(p => ({ ...p, featured: e.target.checked }))}
                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="recipe-featured-checkbox"
                  />
                  <Label htmlFor="featured" className="text-base cursor-pointer">
                    ⭐ {getLocalizedText({ fi: 'Suositeltu resepti', fr: 'Recette vedette', en: 'Featured recipe' })}
                  </Label>
                </div>
              </div>

              {/* Time & Servings */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.recipePrepTime')}</Label>
                  <Input
                    type="number"
                    value={formData.prep_time}
                    onChange={(e) => setFormData(p => ({ ...p, prep_time: parseInt(e.target.value) || 0 }))}
                    min={0}
                    data-testid="recipe-prep-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.recipeCookTime')}</Label>
                  <Input
                    type="number"
                    value={formData.cook_time}
                    onChange={(e) => setFormData(p => ({ ...p, cook_time: parseInt(e.target.value) || 0 }))}
                    min={0}
                    data-testid="recipe-cook-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.recipeServings')}</Label>
                  <Input
                    type="number"
                    value={formData.servings}
                    onChange={(e) => setFormData(p => ({ ...p, servings: parseInt(e.target.value) || 1 }))}
                    min={1}
                    data-testid="recipe-servings-input"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label>{t('admin.recipeImage')}</Label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData(p => ({ ...p, image_url: e.target.value, image_data: '' }))}
                      placeholder="Image URL (or upload below)"
                      data-testid="recipe-image-url-input"
                    />
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-image-btn"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {t('admin.uploadImage')}
                  </Button>
                </div>
                {(formData.image_data || formData.image_url) && (
                  <div className="mt-2 rounded-lg overflow-hidden w-32 h-20">
                    <img 
                      src={formData.image_data || formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('admin.cancel')}
                </Button>
                <Button type="submit" className="bg-primary" data-testid="save-recipe-btn">
                  {t('admin.save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('admin.deleteRecipe')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('admin.confirmDelete')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('admin.cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive hover:bg-destructive/90"
                data-testid="confirm-delete-btn"
              >
                {t('admin.deleteRecipe')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete User Confirmation */}
        <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {getLocalizedText({ fi: 'Poista käyttäjä', fr: "Supprimer l'utilisateur", en: 'Delete User' })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {getLocalizedText({ 
                  fi: `Haluatko varmasti poistaa käyttäjän ${selectedUser?.name}? Tämä poistaa myös kaikki heidän arvostelunsa ja kommenttinsa.`, 
                  fr: `Voulez-vous vraiment supprimer ${selectedUser?.name}? Cela supprimera également tous ses avis et commentaires.`, 
                  en: `Are you sure you want to delete ${selectedUser?.name}? This will also delete all their ratings and comments.` 
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('admin.cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteUser}
                className="bg-destructive hover:bg-destructive/90"
                data-testid="confirm-delete-user-btn"
              >
                {getLocalizedText({ fi: 'Poista', fr: 'Supprimer', en: 'Delete' })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
